import _ from 'lodash'
import { useEffect, useRef } from 'react'
import hot from 'react-hot-toast'

import { useAppSelector } from '../../../store'
import { AlertNotification, CarePlanNotification, IneligiblePatientNotification, SymptomNotification, IgaoNotification, FileNotification } from '../../notifications'

interface Toast {
  id: string
  type: string
}

interface Entry extends Record<string, unknown> {
  id: string
}

function toastify (entry: Entry): Toast {
  let props: Record<string, unknown> = {}

  if (
    'payload' in entry &&
    typeof entry.payload === 'object' &&
    entry.payload !== null
  ) {
    props = {
      ...props,
      ...Object.entries(entry.payload).reduce((acc, [ k, v ]) => ({
        ...acc,
        [_.camelCase(k)]: v
      }), {})
    }
  }

  props = {
    ...props,
    ...Object.entries(entry).reduce((acc, [ k, v ]) => ({
      ...acc,
      [_.camelCase(k)]: v
    }), {})
  }

  return {
    ...props,
    id: entry.id,
    type: typeof props.type === 'string' ? props.type : 'unknown'
  }
}

export function ToastStack (): JSX.Element | null {
  let stack = useAppSelector<Entry[]>(state => {
    // @ts-expect-error // it's okay
    return state.toast.toastStack
  })

  let stackRef = useRef(stack)

  useEffect(() => {
    let toasts = stack.filter(t1 => !stackRef.current.some(t2 => t1.id === t2.id))
    stackRef.current = stack

    for (let entry of toasts) {
      let toast = toastify(entry)
      let NotificationComponent: null | React.ComponentType<any> = null

      switch (toast.type) {
        case 'alert':
        case 'alert_escalated':
          NotificationComponent = AlertNotification
          break

        case 'symptoms_reported':
          NotificationComponent = SymptomNotification
          break

        case 'issue_assigned':
          NotificationComponent = CarePlanNotification
          break

        case 'igao_published':
          NotificationComponent = IgaoNotification
          break

        case 'patient_ineligible':
          NotificationComponent = IneligiblePatientNotification
          break

        case 'new_file':
          NotificationComponent = FileNotification
          break
      }

      hot.custom(t => {
        if (!NotificationComponent) {
          return null
        }

        return (
          <NotificationComponent
            {...t}
            {...toast}
            onDismiss={() => {
              hot.dismiss(t.id)
            }}
          />
        )
      }, {
        id: entry.id,
        duration: 10_000,
        position: 'top-right'
      })
    }
  }, [ stack ])

  return null
}
